import qs from 'qs';
import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';
import $utils from '@/utils';
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const state = {
    invoice: null,
    invoice_loading: false,
    invoice_loading_errors: null,

    invoiceitems: [],
    invoiceitems_count: 0,
    invoiceitems_filters: { offset: 0, limit: 20, ordering: 'position' },
    invoiceitems_loading: false,
    invoiceitems_loading_errors: null,
    invoiceitems_loaded: false,
    invoiceitems_cancel_source: null,

    invoice_saving: false,
    invoice_saving_errors: {},

    invoicefile_uploading: false,
    invoicefile_uploading_errors: null,

    entities: [],
    entities_loading: false,
    entities_loading_errors: null,

    billingaccount: null,
    billingaccount_loading: false,
    billingaccount_loading_errors: null,

    marking_invoice_payment: false,
    validating_invoice: false,
    invalidating_invoice: false,

    events: [],
    events_filters: { offset: 0, limit: 20 },
    events_count: 0,
    events_loading: false,
    events_errors: null,
};

const mutations = {

    updateInvoice(state, invoice) {
        if (invoice && state.invoice && state.invoice.id == invoice.id) {
            state.invoice = invoice;
            return;
        }

        state.invoice = invoice;
        state.invoiceitems = [];
        state.invoiceitems_count = 0;
        state.invoiceitems_loaded = false;
        state.invoice_saving_errors = {};

        if (!invoice) {
            state.invoiceitems_filters = { offset: 0, limit: state.invoiceitems_filters.limit, ordering: 'position' };
            state.events_filters = { offset: 0, limit: state.events_filters.limit, ordering: "-event_date"  };
            return;
        }

        const persistedItemsFilters = loadFiltersFromStorage(getStorageKey('providerinvoice', 'items'), { limit: 20, ordering: 'position' });

        state.invoiceitems_filters = {
            offset: 0,
            limit: persistedItemsFilters.limit,
            ordering: persistedItemsFilters.ordering === 'billing_date,id'
                ? 'position'
                : persistedItemsFilters.ordering,
            invoice: invoice.id,
        }

        state.events_filters = {
            offset: 0,
            limit: state.events_filters.limit,
            object_type: 'providerinvoice', object_id: state.invoice.id, ordering: "-event_date" 
        }
    },
    updateInvoiceLoading(state, loading) {
        state.invoice_loading = loading;
    },
    updateInvoiceLoadingErrors(state, errors) {
        state.invoice_loading_errors = errors;
    },

    updateInvoiceItemSaving(state, invoice_saving) {
        state.invoice_saving = invoice_saving;
    },
    updateInvoiceItemSavingErrors(state, invoice_saving_errors) {
        state.invoice_saving_errors = invoice_saving_errors;
        state.invoice_saving = false;
    },

    updateInvoiceItems(state, invoiceitems) {
        state.invoiceitems = invoiceitems;
    },
    updateInvoiceItemsLoading(state, loading) {
        state.invoiceitems_loading = loading;
    },
    updateInvoiceItemsCount(state, count) {
        state.invoiceitems_count = count;
    },
    updateInvoiceItemsLoadingErrors(state, errors) {
        state.invoiceitems_loading_errors = errors;
    },
    updateInvoiceItemsFilters(state, filters) {
        state.invoiceitems_filters = filters;
        saveFiltersToStorage(getStorageKey('providerinvoice', 'items'), filters, ['limit', 'ordering']);
    },
    updateInvoiceItemsLoaded(state, loaded) {
        state.invoiceitems_loaded = loaded;
    },
    updateInvoiceItemsCancelSource(state, cancelSource) {
        state.invoiceitems_cancel_source = cancelSource;
    },


    updateEntities(state, entities) {
        state.entities = entities;
        state.entities_loading = false;
        state.entities_loading_errors = null;
    },
    updateEntitiesLoading(state, loading) {
        state.entities_loading = loading;
        state.entities_loading_errors = null;
    },
    updateEntitiesLoadingErrors(state, errors) {
        state.entities_loading = false;
        state.entities_loading_errors = errors;
    },

    updateBillingAccount(state, billingaccount) {
        state.billingaccount = billingaccount;
        state.billingaccount_loading = false;
        state.billingaccount_loading_errors = null;
    },
    updateBillingAccountLoading(state, billingaccount_loading) {
        state.billingaccount_loading = billingaccount_loading;
    },
    updateBillingAccountLoadingErrors(state, billingaccount_loading_errors) {
        state.billingaccount = [];
        state.billingaccount_loading = false;
        state.billingaccount_loading_errors = billingaccount_loading_errors;
    },

    updateInvoiceFileUploading(state, uploading) {
        state.invoicefile_uploading = uploading;
    },
    updateInvoiceFileUploadingErrors(state, errors) {
        state.invoicefile_uploading_errors = errors;
    },
    updateValidatingInvoice(state, validating_invoice) {
        state.validating_invoice = validating_invoice;
    },
    updateInvalidatingInvoice(state, invalidating_invoice) {
        state.invalidating_invoice = invalidating_invoice;
    },

    updateMarkingInvoicePayment(state, marking_invoice_payment) {
        state.marking_invoice_payment = marking_invoice_payment;
    },

    updateEvents(state, events) {
        state.events = events;
    },
    updateEventsLoading(state, loading) {
        state.events_loading = loading;
    },
    updateEventsCount(state, count) {
        state.events_count = count;
    },
    updateEventsErrors(state, errors) {
        state.events_errors = errors;
    },
    updateEventsFilters(state, filters) {
        state.events_filters = filters;
    },
};

const actions = {

    async duplicateInvoice({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`/api/providerinvoices/${state.invoice.id}/duplicate/`);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            await dispatch("session/fetchStats", null, { root: true });
        }
    },

    async validateInvoice({ commit, dispatch, state }, params) {
        commit('updateValidatingInvoice', true);
        try {
            const response = await axios.post(`/api/providerinvoices/${state.invoice.id}/validate/`);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateValidatingInvoice', false);
            dispatch('fetchInvoice');
            await dispatch("session/fetchStats", null, { root: true });
        }
    },


    async invalidateInvoice({ commit, dispatch, state }, params) {
        commit('updateInvalidatingInvoice', true);
        try {
            const response = await axios.post(`/api/providerinvoices/${state.invoice.id}/invalidate/`);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateInvalidatingInvoice', false);
            dispatch('fetchInvoice');
            await dispatch("session/fetchStats", null, { root: true });
        }
    },

    async fetchBillingAccount({ commit, dispatch, state, rootGetters }, params) {
        if (rootGetters['session/current_user_permissions'].indexOf("billing.view_billingaccount") === -1) {
            return;
        }

        if (state.invoice.biller != store.getters['session/current_entity_id']) {
            return;
        }

        commit('updateBillingAccountLoading', true);
        commit('updateBillingAccountLoadingErrors', null);
        try {
            const response = await axios.get(`/api/billingaccounts/${state.invoice.billingaccount}/`);
            commit('updateBillingAccount', response.data);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateBillingAccountLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateBillingAccountLoading', false);
        }
    },


    async fetchInvoice({ commit, dispatch, state }, params) {
        await commit('updateInvoiceLoading', true);
        await commit('updateInvoiceLoadingErrors', null);
        let url;
        if (params && params.invoice_id) {
            url = `/api/providerinvoices/${params.invoice_id}/`;
        } else if (state.invoice) {
            url = `/api/providerinvoices/${state.invoice.id}/`;
        } else {
            throw 'No invoice to fetch';
        }
        try {
            const response = await axios.get(url);
            await commit('updateInvoiceLoading', false);
            await commit('updateInvoice', response.data);
            dispatch('fetchInvoiceItems');
            dispatch('fetchBillingAccount');
            dispatch('fetchEvents');
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateInvoiceLoading', false);
            commit('updateInvoiceLoadingErrors', error.details);
            throw error;
        }
    },

    async fetchContractLine({ commit, dispatch, state }, params) {

        let url = `/api/contractlines/${params.contractline_id}/`;
        try {
            const response = await axios.get(url);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async deleteInvoice({ commit, dispatch, state }, params) {
        try {
            const response = await axios.delete(`/api/providerinvoices/${state.invoice.id}/`);
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            dispatch('session/fetchStats', null, { root: true });
            dispatch('billingstats/fetchAllStats', null, { root: true });
        }
    },


    async deleteInvoiceItem({ commit, dispatch, state }, params) {
        try {
            const response = await axios.delete(`/api/providerinvoiceitems/${params.instance.id}/`);
            dispatch('fetchInvoice');
            dispatch('fetchInvoiceItems');
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            dispatch('session/fetchStats', null, { root: true });
            dispatch('billingstats/fetchAllStats', null, { root: true });
        }
    },


    async fetchInvoiceItems({ commit, dispatch, state }, params) {
        commit('updateInvoiceItemsLoading', true);
        commit('updateInvoiceItemsLoadingErrors', null);

        if (state.invoiceitems_cancel_source) {
            state.invoiceitems_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateInvoiceItemsCancelSource', cancelSource);

        try {
            const response = await axios.get("/api/providerinvoiceitems/", {
                params: state.invoiceitems_filters,
                cancelToken: state.invoiceitems_cancel_source?.token,
            });
            commit('updateInvoiceItems', response.data.results);
            commit('updateInvoiceItemsCount', response.data.count);
            commit('updateInvoiceItemsCancelSource', null);
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            let error = utils.handleError(xhr_error);
            commit('updateInvoiceItemsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateInvoiceItemsLoading', false);
        }
    },

    exportInvoiceItemsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        filters.invoice = state.invoice.id;
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        filters.ordering = 'position';
        
        $utils.downloadFile({
            url: "/api/providerinvoiceitems/",
            params: filters,
        }, $utils.getFilename('providerInvoiceItems', { invoice_number: state.invoice?.invoice_id || state.invoice?.id || '' }));
    },

    async uploadInvoiceFile({ commit, dispatch, state }, params) {
        let formData = new FormData();
        formData.append('invoice_file', params.invoice_file);

        try {
            commit('updateInvoiceFileUploading', true);
            commit('updateInvoiceFileUploadingErrors', null);
            const response = await axios.post(`/api/providerinvoices/${state.invoice.id}/invoicefile/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateInvoiceFileUploadingErrors', error.details);
            throw error;
        } finally {
            commit('updateInvoiceFileUploading', false);
            dispatch('fetchInvoice');
        }
    },


    async markInvoicePayment({ commit, dispatch, state }, params) {
        try {
            commit('updateMarkingInvoicePayment', true);
            const response = await axios.post(
                `/api/providerinvoices/${state.invoice.id}/paid/`,
                { paid: params.paid }
            );
            dispatch("session/fetchStats", null, { root: true });
            dispatch("billingstats/fetchAllStats", null, { root: true });
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateMarkingInvoicePayment', false);
        }
    },

    async archiveInvoice({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`/api/providerinvoices/${state.invoice.id}/archive/`);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },


    async unarchiveInvoice({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(`/api/providerinvoices/${state.invoice.id}/unarchive/`);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async init({ commit, dispatch, state }, params) {
        await commit('updateInvoice', params.invoice);
        dispatch('fetchEvents');
    },

    async fetchEvents({ commit, dispatch, state }) {
        commit('updateEventsLoading', true);

        try {
            const response = await axios.get('/api/events/', { params: state.events_filters });
            commit('updateEvents', response.data.results);
            commit('updateEventsCount', response.data.count);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateEventsErrors', error.details);
            throw error;
        } finally {
            commit('updateEventsLoading', false);
        }
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
};
